import { 
  ScenarioTemplate,
  TagDefinition,
  CategoryHierarchy,
  ScenarioDependency 
} from '../types/ScenarioManagement';

// Local type aliases for compatibility
type ScenarioCategory = CategoryHierarchy;
type ScenarioTag = TagDefinition;

interface ScenarioData {
  scenarios: [string, any][];
  templates: [string, ScenarioTemplate][];
  categories: [string, ScenarioCategory][];
  tags: [string, ScenarioTag][];
  dependencies: [string, ScenarioDependency][];
  globalInvestments?: [string, any][];
  version?: string;
  exportedAt?: string;
  metadata?: {
    totalScenarios: number;
    totalInvestments: number;
    appVersion: string;
  };
}


export class FilePersistenceService {
  private static instance: FilePersistenceService;
  private readonly APP_VERSION = '1.0.0';

  private constructor() {}

  static getInstance(): FilePersistenceService {
    if (!FilePersistenceService.instance) {
      FilePersistenceService.instance = new FilePersistenceService();
    }
    return FilePersistenceService.instance;
  }

  /**
   * Export scenario data to downloadable JSON file
   */
  async exportToFile(data: ScenarioData, filename?: string): Promise<void> {
    try {
      const exportData: ScenarioData = {
        ...data,
        version: this.APP_VERSION,
        exportedAt: new Date().toISOString(),
        metadata: {
          totalScenarios: data.scenarios.length,
          totalInvestments: data.globalInvestments?.length || 0,
          appVersion: this.APP_VERSION
        }
      };

      const jsonStr = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const defaultFilename = `risk-game-scenarios-${timestamp}.json`;
      
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || defaultFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      console.log('Scenario data exported successfully');
    } catch (error) {
      console.error('Failed to export scenario data:', error);
      throw new Error('Failed to export scenario data');
    }
  }

  /**
   * Import scenario data from uploaded file
   */
  async importFromFile(file: File): Promise<ScenarioData> {
    try {
      const fileContent = await this.readFileContent(file);
      const importData = JSON.parse(fileContent) as ScenarioData;
      
      // Validate imported data structure
      this.validateImportedData(importData);
      
      console.log('Scenario data imported successfully:', {
        scenarios: importData.scenarios?.length || 0,
        investments: importData.globalInvestments?.length || 0,
        version: importData.version
      });

      return importData;
    } catch (error) {
      console.error('Failed to import scenario data:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to import scenario data');
    }
  }

  /**
   * Read file content as text
   */
  private readFileContent(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        if (event.target?.result) {
          resolve(event.target.result as string);
        } else {
          reject(new Error('Failed to read file content'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('File reading error'));
      };
      
      reader.readAsText(file);
    });
  }

  /**
   * Validate imported data structure
   */
  private validateImportedData(data: any): void {
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid file format: not a valid JSON object');
    }

    // Check for required fields
    const requiredFields = ['scenarios', 'templates', 'categories', 'tags', 'dependencies'];
    for (const field of requiredFields) {
      if (!Array.isArray(data[field])) {
        throw new Error(`Invalid file format: ${field} must be an array`);
      }
    }

    // Validate scenarios array format
    if (data.scenarios.length > 0) {
      const firstScenario = data.scenarios[0];
      if (!Array.isArray(firstScenario) || firstScenario.length !== 2) {
        throw new Error('Invalid file format: scenarios must be in [id, data] format');
      }
    }

    // Validate global investments if present
    if (data.globalInvestments !== undefined && !Array.isArray(data.globalInvestments)) {
      throw new Error('Invalid file format: globalInvestments must be an array');
    }

    console.log('Import data validation passed');
  }

  /**
   * Merge imported data with existing data
   */
  async mergeImportedData(
    existingData: ScenarioData, 
    importedData: ScenarioData,
    options: {
      overwriteExisting: boolean;
      includeInvestments: boolean;
      includeCategories: boolean;
      includeTags: boolean;
    } = {
      overwriteExisting: false,
      includeInvestments: true,
      includeCategories: true,
      includeTags: true
    }
  ): Promise<ScenarioData> {
    try {
      const mergedData: ScenarioData = {
        ...existingData,
        version: this.APP_VERSION
      };

      // Merge scenarios
      const existingScenarioMap = new Map(existingData.scenarios);
      let addedCount = 0;
      let updatedCount = 0;

      for (const [id, scenario] of importedData.scenarios) {
        if (existingScenarioMap.has(id)) {
          if (options.overwriteExisting) {
            existingScenarioMap.set(id, {
              ...scenario,
              updatedAt: new Date(),
              version: (scenario.version || 0) + 1
            });
            updatedCount++;
          }
        } else {
          existingScenarioMap.set(id, {
            ...scenario,
            createdAt: new Date(),
            updatedAt: new Date()
          });
          addedCount++;
        }
      }

      mergedData.scenarios = Array.from(existingScenarioMap.entries());

      // Merge global investments
      if (options.includeInvestments && importedData.globalInvestments) {
        const existingInvestmentMap = new Map(existingData.globalInvestments || []);
        
        for (const [id, investment] of importedData.globalInvestments) {
          if (!existingInvestmentMap.has(id) || options.overwriteExisting) {
            existingInvestmentMap.set(id, {
              ...investment,
              updatedAt: new Date()
            });
          }
        }

        mergedData.globalInvestments = Array.from(existingInvestmentMap.entries());
      }

      // Merge categories
      if (options.includeCategories && importedData.categories) {
        const existingCategoryMap = new Map(existingData.categories);
        
        for (const [id, category] of importedData.categories) {
          if (!existingCategoryMap.has(id) || options.overwriteExisting) {
            existingCategoryMap.set(id, category);
          }
        }

        mergedData.categories = Array.from(existingCategoryMap.entries());
      }

      // Merge tags
      if (options.includeTags && importedData.tags) {
        const existingTagMap = new Map(existingData.tags);
        
        for (const [id, tag] of importedData.tags) {
          if (!existingTagMap.has(id) || options.overwriteExisting) {
            existingTagMap.set(id, tag);
          }
        }

        mergedData.tags = Array.from(existingTagMap.entries());
      }

      // Merge templates (always include)
      const existingTemplateMap = new Map(existingData.templates);
      for (const [id, template] of importedData.templates) {
        if (!existingTemplateMap.has(id) || options.overwriteExisting) {
          existingTemplateMap.set(id, template);
        }
      }
      mergedData.templates = Array.from(existingTemplateMap.entries());

      // Merge dependencies (always include)
      const existingDependencyMap = new Map(existingData.dependencies);
      for (const [id, dependency] of importedData.dependencies) {
        if (!existingDependencyMap.has(id) || options.overwriteExisting) {
          existingDependencyMap.set(id, dependency);
        }
      }
      mergedData.dependencies = Array.from(existingDependencyMap.entries());

      console.log('Data merge completed:', {
        scenariosAdded: addedCount,
        scenariosUpdated: updatedCount,
        totalScenarios: mergedData.scenarios.length
      });

      return mergedData;
    } catch (error) {
      console.error('Failed to merge imported data:', error);
      throw new Error('Failed to merge imported data');
    }
  }


  /**
   * Validate file before import
   */
  async validateFile(file: File): Promise<{
    isValid: boolean;
    errors: string[];
    warnings: string[];
    summary?: {
      scenarios: number;
      investments: number;
      version: string;
    };
  }> {
    const errors: string[] = [];
    const warnings: string[] = [];

    try {
      // Check file type
      if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
        errors.push('File must be a JSON file');
      }

      // Check file size (max 50MB)
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        errors.push('File size exceeds 50MB limit');
      }

      if (errors.length > 0) {
        return { isValid: false, errors, warnings };
      }

      // Try to parse and validate content
      const content = await this.readFileContent(file);
      const data = JSON.parse(content);
      
      this.validateImportedData(data);

      // Check version compatibility
      if (data.version && data.version !== this.APP_VERSION) {
        warnings.push(`File version (${data.version}) differs from app version (${this.APP_VERSION})`);
      }

      // Check for scenarios
      if (!data.scenarios || data.scenarios.length === 0) {
        warnings.push('No scenarios found in file');
      }

      return {
        isValid: true,
        errors,
        warnings,
        summary: {
          scenarios: data.scenarios?.length || 0,
          investments: data.globalInvestments?.length || 0,
          version: data.version || 'unknown'
        }
      };

    } catch (error) {
      errors.push(`Invalid JSON file: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return { isValid: false, errors, warnings };
    }
  }

  /**
   * Generate checksum for data integrity
   */
  private generateChecksum(content: string): string {
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(16);
  }
}